import { AssessmentContent } from '@protos/learning_management/lms.assessment.common.apis_pb';
import {
  AssessmentStudentResponse,
  AssessmentStudentResponseV2,
  StudentAssessAttemptResult,
} from '@protos/learning_management/lms.assessment.student.apis_pb';
import { deserify } from '@geneo2-web/shared-ui';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { set } from 'video.js/dist/types/tech/middleware';

export interface IMultiSubjectFAState {
  active_assessment_id?: string;
  active_assessment_content?: {
    [assessmentId: string]: AssessmentContent | undefined;
  };
  active_assessment_student_response?: {
    [assessmentId: string]: AssessmentStudentResponseV2 | undefined;
  };
  active_assessment_session_id?: bigint;
  active_section_number?: number;
  selected_assessment_data?: {
    assessmentId: bigint;
    questionIds: string[];
    resumeCheck: boolean;
    reattemptedCheck: boolean;
    SubmissionType: number;
    elapsedTime?: number;
  };
  active_assessment_attempt_result?: {
    [assessmentId: string]: StudentAssessAttemptResult | undefined;
  };
  all_answers: string[];
}

const initialState: IMultiSubjectFAState = {
  active_assessment_id: undefined,
  active_assessment_content: undefined,
  active_assessment_student_response: undefined,
  active_section_number: 0,
  selected_assessment_data: undefined,
  active_assessment_attempt_result: undefined,
  all_answers: [],
};

const multisubjectFASlice = createSlice({
  name: 'multisubjectFA',
  initialState: initialState as IMultiSubjectFAState,
  reducers: {
    setActiveAssessmentId: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.active_assessment_id = action.payload;
    },
    setActiveAssessmentContent: (
      state,
      action: PayloadAction<{
        assessmentId: bigint | undefined;
        content?: AssessmentContent;
      }>
    ) => {
      if (action.payload.assessmentId === undefined) return;
      // serifyMiddleware serifies the payload before this reducer runs, so
      // assessmentId arrives as the serified string "BigInt(n)"; deserify it
      // back to a bigint so the map key is the plain id ("n"), matching reads.
      const key = deserify(action.payload.assessmentId).toString();
      state.active_assessment_content = {
        ...(state.active_assessment_content || {}),
        [key]: action.payload.content,
      };
    },
    setActiveAssessmentStudentResponse: (
      state,
      action: PayloadAction<{
        assessmentId: bigint | undefined;
        response?: AssessmentStudentResponseV2;
      }>
    ) => {
      if (action.payload.assessmentId === undefined) return;
      const key = deserify(action.payload.assessmentId).toString();
      state.active_assessment_student_response = {
        ...(state.active_assessment_student_response || {}),
        [key]: action.payload.response,
      };
    },

    setActiveAssessmentSessionId: (state, action) => {
      state.active_assessment_session_id = action.payload;
    },
    setActiveSectionNumber: (state, action: PayloadAction<number>) => {
      state.active_section_number = action.payload;
    },
    setSelectedAssessmentData: (state, action) => {
      state.selected_assessment_data = action.payload;
    },
    setActiveAssessmentAttemptResult: (
      state,
      action: PayloadAction<{
        assessmentId: bigint | undefined;
        result?: StudentAssessAttemptResult;
      }>
    ) => {
      if (action.payload.assessmentId === undefined) return;
      const key = deserify(action.payload.assessmentId).toString();
      state.active_assessment_attempt_result = {
        ...state.active_assessment_attempt_result,
        [key]: action.payload.result,
      };
    },
    setAllAnswers: (state, action) => {
      state.all_answers = action.payload;
    },
    resetMultiSubjectFAState: () => initialState,
  },
});

export const {
  setActiveAssessmentId,
  setActiveAssessmentContent,
  setActiveAssessmentStudentResponse,
  setActiveSectionNumber,
  setActiveAssessmentSessionId,
  setSelectedAssessmentData,
  resetMultiSubjectFAState,
  setActiveAssessmentAttemptResult,
  setAllAnswers,
} = multisubjectFASlice.actions;
export default multisubjectFASlice.reducer;
